import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Schedule,
  ScheduleSummary,
  ScheduleStatus,
} from '../../interfaces/Schedule';
import { getSchedule } from '../../services/Schedule';
import { toastApiError } from '../../utils/ApiError';
import UpdateScheduleDialog from './UpdateScheduleDialog';
import Waiting from '../Waiting';
import useScheduleNavigationGuard from './useScheduleNavigationGuard';

export default function MobileSchedulePage({
  id,
  scheduleSummaries,
  onModify,
}: {
  id: number;
  scheduleSummaries: ScheduleSummary[];
  onModify: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [schedule, setSchedule] = useState<Schedule>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const dirty = useRef(false);
  const setDirty = useCallback((value: boolean) => {
    dirty.current = value;
  }, []);
  useScheduleNavigationGuard(dirty, t('schedule.mobile.discard'));
  useEffect(() => {
    let active = true;
    setFailed(false);
    getSchedule(id)
      .then((value) => {
        if (active) setSchedule(value);
      })
      .catch((error) => {
        if (active) {
          setFailed(true);
          toastApiError(error);
        }
      });
    return () => {
      active = false;
    };
  }, [id, attempt]);
  const close = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/schedule', { replace: true });
  };
  if (failed)
    return (
      <Stack spacing={2}>
        <Alert severity='error'>{t('schedule.mobile.loadError')}</Alert>
        <Button onClick={() => setAttempt((value) => value + 1)}>
          {t('schedule.mobile.retry')}
        </Button>
        <Button onClick={close}>{t('schedule.mobile.back')}</Button>
      </Stack>
    );
  if (!schedule) return <Waiting open />;
  if (
    schedule.status === ScheduleStatus.QUEUED ||
    schedule.status === ScheduleStatus.RUNNING
  )
    return (
      <Stack spacing={2}>
        <Alert severity='info'>{t('schedule.mobile.unavailable')}</Alert>
        <Button onClick={close}>{t('schedule.mobile.back')}</Button>
      </Stack>
    );
  return (
    <UpdateScheduleDialog
      key={`${location.pathname}-${attempt}`}
      mobilePage
      clickedSchedule={schedule}
      scheduleSummaries={scheduleSummaries}
      onDirtyChange={setDirty}
      onClose={close}
      onModify={() => {
        dirty.current = false;
        onModify();
        close();
      }}
    />
  );
}
